import { FC, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';

import { AnalyticsEvent, useAnalytics } from '@bonusly/analytics';
import { Box, HStack, IconButton, Link, Portal, rem, Text, VStack, XOutline } from '@bonusly/design-system';
import { useCurrentUser } from '@bonusly/who-am-i';

import { NotificationEmptyState, NotificationItem } from '../../../top-navbar/components/Notification';
import { useFetchNotifications } from '../../../top-navbar/components/Notification/hooks/useFetchNotifications';
import { useInvalidateNotifications } from '../../../top-navbar/components/Notification/hooks/useInvalidateNotifications';
import { NotificationUpdateType, useUpdateNotifications } from '../../../top-navbar/components/Notification/hooks/useUpdateNotifications';
import { NotificationStatus } from '../../../top-navbar/lib/notification/notification';

interface NotificationPanelProps {
	isOpen: boolean;
	onClose: () => void;
	sidebarWidth: number;
}

export const NotificationPanel: FC<NotificationPanelProps> = ({ isOpen, onClose, sidebarWidth }) => {
	const { t } = useTranslation('navigation');
	const user = useCurrentUser();
	const invalidate = useInvalidateNotifications();
	const { notifications, unreadCount } = useFetchNotifications(user.locale);
	const { track } = useAnalytics();
	const handleUpdateNotification = useUpdateNotifications('mark-all-read', {});
	const panelRef = useRef<HTMLDivElement>(null);
	// Track if we've already marked notifications as seen for the current open session
	const hasMarkedAsSeenRef = useRef(false);

	// Reset the "marked as seen" flag when panel closes
	useEffect(() => {
		if (!isOpen) {
			hasMarkedAsSeenRef.current = false;
		}
	}, [isOpen]);

	// Handle click outside to close panel
	useEffect(() => {
		if (!isOpen) return;

		const handleClickOutside = (event: MouseEvent) => {
			if (panelRef.current && !panelRef.current.contains(event.target as Node)) {
				onClose();
			}
		};

		// Add listener with a small delay to avoid immediately closing from the click that opened it
		const timeoutId = setTimeout(() => {
			document.addEventListener('mousedown', handleClickOutside);
		}, 0);

		return () => {
			clearTimeout(timeoutId);
			document.removeEventListener('mousedown', handleClickOutside);
		};
	}, [isOpen, onClose]);

	// Track panel view when panel opens
	useEffect(() => {
		if (!isOpen) return;
		track(AnalyticsEvent.TopNavbar.NotificationsViewList);
	}, [isOpen, track]);

	// Mark notifications as seen when panel is open and notifications are loaded
	useEffect(() => {
		if (!isOpen) return;
		if (hasMarkedAsSeenRef.current) return;
		if (unreadCount === 0) return;

		const unReadNotifications = notifications.filter((notification) => notification.status === NotificationStatus.Unread);

		if (unReadNotifications.length === 0) return;

		hasMarkedAsSeenRef.current = true;

		const markAsSeen = async () => {
			const updateNotifications = unReadNotifications.map((notification) =>
				handleUpdateNotification.mutateAsync({
					notificationId: notification.id,
					type: NotificationUpdateType.MarkAsSeen,
				})
			);

			await Promise.all(updateNotifications);
			invalidate();
		};

		markAsSeen();
	}, [isOpen, notifications, unreadCount, handleUpdateNotification, invalidate]);

	const handleMarkAllRead = async () => {
		const unReadNotifications =
			notifications
				?.filter((notification) => notification.status !== NotificationStatus.Read)
				.map((notification) =>
					handleUpdateNotification.mutateAsync({
						notificationId: notification.id,
						type: NotificationUpdateType.MarkAsRead,
					})
				) ?? [];

		await Promise.all(unReadNotifications);
		invalidate();
		track(AnalyticsEvent.TopNavbar.NotificationsMarkAllRead);
	};

	if (!isOpen) {
		return null;
	}

	return (
		<Portal>
			<Box
				ref={panelRef}
				position="fixed"
				top={rem(4)}
				left={`${sidebarWidth + 4}px`}
				bottom={rem(8)}
				width={rem(350)}
				bg="white"
				border="1px solid"
				borderColor="border.default"
				borderRadius="md"
				zIndex="modal"
				overflowY="auto"
				boxShadow="lg"
				py={rem(20)}
				px={rem(8)}
				data-testid="notification-panel"
			>
				<VStack spacing={0} align="stretch">
					{/* Header with title and close button */}
					<HStack justify="space-between" align="center" px={rem(12)} pb={rem(12)}>
						<Text fontSize={200} fontWeight="semibold">
							{t('notification.header')}
						</Text>
						<IconButton
							aria-label={t('notification.close')}
							icon={<XOutline />}
							size="sm"
							bg="secondary.bgAlpha"
							borderRadius="full"
							onClick={onClose}
							data-testid="notification-panel-close"
						/>
					</HStack>

					{/* Mark all as read link - only show if there are notifications not yet read */}
					{notifications.some((notification) => notification.status !== NotificationStatus.Read) && (
						<HStack justify="flex-end" px={rem(8)}>
							<Link onClick={handleMarkAllRead} textColor="primary.fgStrong" as="a" tabIndex={0} cursor="pointer" fontSize="sm">
								{t('notification.read_all')}
							</Link>
						</HStack>
					)}

					{/* Notification list */}
					<VStack gap={0} as="ul" role="listbox" aria-label={t('notification.menu_label')} w="100%">
						{notifications.length > 0 ? (
							notifications.map((notification) => <NotificationItem notification={notification} key={notification.id} handleClose={onClose} />)
						) : (
							<NotificationEmptyState />
						)}
					</VStack>
				</VStack>
			</Box>
		</Portal>
	);
};
